import { MachineType, OperatingSystem } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { deviceDetector, convertDeviceInfoToMachineInfo } from '#~/utils/index.js';

export interface DiscoveredService {
  name: string;
  type: string;
  host: string;
  address: string;
  port: number;
  data?: Record<string, any>;
  deviceType?: {
    os: OperatingSystem;
    machineType: MachineType;
    confidence: 'high' | 'medium' | 'low'; // Confidence level of the determination
  };
}

/**
 * Health of each way the desktop app finds cameras, as pushed by main over
 * `mdns:health` and read on demand over `mdns:getHealth` (ENG-4948). Mirrors
 * studio/electron's DiscoveryHealth. Anything not failed is working.
 */
export type DiscoveryMethod = 'mdns' | 'scan';

export type DiscoveryMethodHealth =
  | { status: 'ok'; since: number }
  | { status: 'failed'; reason: string; since: number };

export type DiscoveryHealth = Record<DiscoveryMethod, DiscoveryMethodHealth>;

/**
 * Type that maps a DiscoveredService to an Agent-like structure
 * This allows using DiscoveredService in components that expect Agent data
 * without having to modify the components
 */
export interface DiscoveredServiceAsAgent {
  id: string; // Using address or host as unique identifier
  name: string; // Using the name from DiscoveredService
  status: 'online' | 'offline'; // Always 'online' for discovered services
  // Additional properties that might be needed to match Agent interface
  address: string;
  port: number;
  type: string;
  host: string;
  isDiscoveredService: true; // Flag to identify this as a discovered service
  data?: Record<string, string>;
  deviceType?: {
    os: OperatingSystem;
    machineType: MachineType;
    confidence: 'high' | 'medium' | 'low';
  };
}

/**
 * Detects the current browser/client OS and machine type using the unified DeviceDetector
 * This replaces the old Web API detection with the comprehensive DeviceDetector implementation
 *
 *  ? Not currently being used or referenced
 */
export const detectCurrentMachineInfo = (): {
  os: OperatingSystem;
  machineType: MachineType;
  confidence: 'high' | 'medium' | 'low';
} => {
  // Use the unified device detector for much more accurate detection
  const deviceInfo = deviceDetector.detectDevice();
  return convertDeviceInfoToMachineInfo(deviceInfo);
};

/**
 * OS detection patterns used consistently across all detection functions
 */
const OS_DETECTION_PATTERNS = {
  windows: [
    /\bwindows\b/i,
    /\bwin\b/i, // word boundary to avoid matching "darwin", "twin", etc.
    /\bwin32\b/i, // word boundary to avoid matching "darwin", "twin", etc.
    /\bwin64\b/i, // word boundary to avoid matching "darwin", "twin", etc.
    /\bmicrosoft\b/i,
    /\bsurface\b/i,
    /\bpc\b/i
  ],
  mac: [
    /\bmac(?:os)?\b/i,
    /\bapple\b/i,
    /\bdarwin\b/i, // macOS kernel name
    /\bimac\b/i,
    /\bmacbook\b/i,
    /\bmacmini\b/i,
    /\bmacpro\b/i
  ],
  linux: [
    /\blinux\b/i,
    /\bubuntu\b/i,
    /\bdebian\b/i,
    /\bfedora\b/i,
    /\bcentos\b/i,
    /\brhel\b/i,
    /\barch(?:linux)?\b/i,
    /\bmanjaro\b/i
  ]
} as const;

/**
 * Helper function to check if a string matches any pattern in a list
 */
const matchesAnyPattern = (text: string, patterns: readonly (string | RegExp)[]): boolean => {
  const lowerText = text.toLowerCase();
  return patterns.some((pattern) =>
    pattern instanceof RegExp ? pattern.test(lowerText) : lowerText.includes(pattern.toLowerCase())
  );
};

/**
 * Deterministically detects OS from a string using consistent patterns
 */
const detectOSFromString = (text: string): OperatingSystem => {
  if (!text) return OperatingSystem.OS_MAC; // default

  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.windows)) {
    return OperatingSystem.OS_WINDOWS;
  }
  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.mac)) {
    return OperatingSystem.OS_MAC;
  }
  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.linux)) {
    return OperatingSystem.OS_LINUX;
  }

  return OperatingSystem.OS_MAC; // default to mac for imagery when unspecified
};

/**
 * Detects OS from a string but returns undefined if no match is found
 */
const detectOSFromStringOrUndefined = (text: string): OperatingSystem | undefined => {
  if (!text) return undefined;

  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.windows)) {
    return OperatingSystem.OS_WINDOWS;
  }
  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.mac)) {
    return OperatingSystem.OS_MAC;
  }
  if (matchesAnyPattern(text, OS_DETECTION_PATTERNS.linux)) {
    return OperatingSystem.OS_LINUX;
  }

  return undefined;
};

/**
 * Deterministic device type determination for discovered services using mDNS TXT fields
 * Primary source is the `machineType` field coming from Bonjour packets as per ENG-2865
 * Falls back conservatively only if the field is missing.
 */
export const determineServiceDeviceType = (
  service: DiscoveredService
): { os: OperatingSystem; machineType: MachineType; confidence: 'high' | 'medium' | 'low' } => {
  // Helper to map machineType strings (e.g., "Windows Laptop", "iMac", "Mac Mini") deterministically
  const mapFromMachineTypeString = (
    raw: string
  ): { os: OperatingSystem; machineType: MachineType } | null => {
    if (!raw) return null;
    const s = raw.toLowerCase().trim();

    // OS detection using consistent patterns
    const isWindows = matchesAnyPattern(s, OS_DETECTION_PATTERNS.windows);
    const isMac = matchesAnyPattern(s, OS_DETECTION_PATTERNS.mac);
    const isLinux = matchesAnyPattern(s, OS_DETECTION_PATTERNS.linux);

    const os = detectOSFromString(s);

    // Machine form-factor detection
    const isLaptop =
      s.includes('laptop') ||
      s.includes('notebook') ||
      s.includes('book') ||
      s.includes('macbook') ||
      s.includes('surface book');
    const isDesktop =
      s.includes('desktop') ||
      s.includes('imac') ||
      s.includes('mac mini') ||
      s.includes('mac-mini') ||
      s.includes('macmini') ||
      s.includes('mac pro') ||
      s.includes('mac-pro') ||
      s.includes('macpro') ||
      s.includes('nuc');

    const machineType: MachineType = isLaptop
      ? MachineType.MACHINE_TYPE_LAPTOP
      : MachineType.MACHINE_TYPE_DESKTOP;

    // If we could determine anything from the string, return a high-confidence mapping
    if (isLaptop || isDesktop || isWindows || isMac || isLinux) {
      return { os, machineType };
    }
    return null;
  };

  // Map helper function for device name-based detection
  const mapFromDeviceName = (
    s: string,
    sourceRaw: string
  ): { machineType: MachineType; os?: OperatingSystem; confidence: 'high' | 'medium' } | null => {
    if (!s) return null;

    // Strong laptop indicators
    const laptopIndicators = [
      'macbook',
      'laptop',
      'notebook',
      'surface book',
      'thinkpad',
      'xps',
      'zenbook'
    ];

    // Strong desktop indicators
    const desktopIndicators = [
      'desktop',
      'imac',
      'mac mini',
      'mac-mini',
      'macmini',
      'mac pro',
      'mac-pro',
      'macpro'
    ];

    const has = (tokens: string[]) => tokens.some((t) => s.includes(t));

    const isLaptop = has(laptopIndicators) || /^laptop-[a-z0-9]+/i.test(sourceRaw);
    const isDesktop = has(desktopIndicators) || /^desktop-[a-z0-9]+/i.test(sourceRaw);

    if (!(isLaptop || isDesktop)) return null;

    // Optional OS inference from name using consistent patterns
    const os = detectOSFromStringOrUndefined(s);

    return {
      machineType: isLaptop ? MachineType.MACHINE_TYPE_LAPTOP : MachineType.MACHINE_TYPE_DESKTOP,
      ...(os && { os }),
      confidence: 'high'
    };
  };

  // Primary source: Try TXT record machineType field first
  // This field comes from the bridge/agent and should be reliable when present
  const txtMachineType = service?.data?.machineType as string | undefined;
  const mapped = txtMachineType ? mapFromMachineTypeString(txtMachineType) : null;
  if (mapped) {
    return { ...mapped, confidence: 'high' };
  }

  // Secondary source: Use actual service name for device type inference
  // This is often more reliable than mDNS machineType field which can be incorrect
  // Examples: "Iris's Mac mini", "someones MacBook Pro"
  const serviceName = service?.name || '';
  const serviceNameLower = serviceName.toLowerCase().trim();
  const fromServiceName = mapFromDeviceName(serviceNameLower, serviceName);

  // Optional secondary hint: explicit OS field if provided by TXT records
  const osField = (service?.data?.os as string | undefined)?.toLowerCase();

  // Use consistent patterns for OS field detection
  const osFromTxt = detectOSFromStringOrUndefined(osField || '');

  if (fromServiceName) {
    const result = {
      os: fromServiceName.os ?? osFromTxt ?? OperatingSystem.OS_MAC,
      machineType: fromServiceName.machineType,
      confidence: fromServiceName.confidence
    };
    return result;
  }

  // Conservative default without trying to infer from local/browser, hostnames, or service name...
  return {
    os: osFromTxt ?? OperatingSystem.OS_MAC,
    machineType: MachineType.MACHINE_TYPE_DESKTOP,
    confidence: 'low'
  };
};

/**
 * Deterministic device type determination that prioritizes mDNS TXT fields only.
 * No hostname heuristics or local/browser fallbacks.
 */
export const enhanceServiceDeviceTypeWithUnifiedDetection = (
  service: DiscoveredService
): { os: OperatingSystem; machineType: MachineType; confidence: 'high' | 'medium' | 'low' } => {
  return determineServiceDeviceType(service);
};

/**
 * Enhances a discovered service with proper device type information
 * Now uses the enhanced detection that prioritizes unified detection
 */
export const enhanceDiscoveredService = (service: DiscoveredService): DiscoveredService => {
  const deviceType = enhanceServiceDeviceTypeWithUnifiedDetection(service);
  return {
    ...service,
    deviceType
  };
};

/**
 * Converts an array of DiscoveredService objects to an array of Agent-like objects
 * ? Not currently being used or referenced
 *
 * @param services - The discovered services to convert
 * @returns An array of Agent-like representations of the discovered services
 */
export function discoveredServicesToAgents(
  services: DiscoveredService[]
): DiscoveredServiceAsAgent[] {
  return services.map(discoveredServiceToAgent);
}

/**
 * Converts a DiscoveredService to an Agent-like structure
 *
 * @param service - The discovered service to convert
 * @returns An Agent-like representation of the discovered service
 */
export function discoveredServiceToAgent(service: DiscoveredService): DiscoveredServiceAsAgent {
  // Extract bridgeType from txt records if available
  const bridgeType = service.data?.bridgeType as 'd' | 'e' | null;

  // Enhance the service with device type information if not already present
  const enhancedService = service.deviceType ? service : enhanceDiscoveredService(service);

  return {
    id: `${service.address}:${service.port}`, // Create a unique ID from address and port
    name: normalizeHostname(service.host),
    status: 'online', // Discovered services are always online
    address: service.address,
    port: service.port,
    type: service.type,
    host: service.host,
    isDiscoveredService: true,
    ...(service.data && { data: service.data }),
    ...(bridgeType && { bridgeType }),
    ...(enhancedService.deviceType && { deviceType: enhancedService.deviceType })
  };
}

/**
 * Normalizes a hostname from mDNS discovery to make it more human-readable
 *
 * @param hostname - The hostname to normalize
 * @returns A normalized, human-readable hostname
 */
export function normalizeHostname(hostname: string): string {
  if (!hostname) {
    return '';
  }

  // Remove any protocol prefix
  return (
    hostname
      .replace(/^(https?:\/\/)/i, '') // Remove protocol
      .replace(/:\d+$/, '') // Remove port
      .replace(/%[^.]+/g, '') // Remove IPv6 scope IDs
      .replace(/\.local\.local$/i, '') // Remove double .local.local suffix
      .replace(/\.local\.?$/i, '') // Remove .local suffix
      .replace(/\.lan$/i, '') // Remove .lan suffix
      // Windows specific patterns
      .replace(/^DESKTOP-[A-Z0-9]+/i, '') // Remove Windows default desktop names
      .replace(/^LAPTOP-[A-Z0-9]+/i, '') // Remove Windows default laptop names
      .replace(/^WIN-[A-Z0-9]+/i, '') // Remove Windows server default names
      .replace(/^WORKSTATION-[A-Z0-9]+/i, '') // Remove Windows workstation names
      .replace(/^SURFACE-[A-Z0-9]+/i, '') // Remove Surface device names
      .replace(/\.workgroup$/i, '') // Remove Windows workgroup suffix
      .replace(/\.mshome\.net$/i, '') // Remove Windows home network suffix
      // macOS/Linux specific patterns
      .replace(/^MacBook-(Pro|Air)-[A-Za-z0-9]+/i, '') // Remove MacBook default names
      .replace(/^iMac-[A-Za-z0-9]+/i, '') // Remove iMac default names
      .replace(/^Mac-[A-Za-z0-9]+/i, '') // Remove Mac default names
      .replace(/^MacPro-[A-Za-z0-9]+/i, '') // Remove Mac Pro default names
      .replace(/^MacMini-[A-Za-z0-9]+/i, '') // Remove Mac Mini default names
      // Additional network/domain suffixes
      .replace(/\.(?:home|internal|localdomain|private|corp|intranet)$/i, '') // Remove additional network suffixes
      .replace(/\.(?:home\.arpa|local\.arpa)$/i, '') // Remove mDNS suffixes
      .replace(/\.(?:[a-z0-9-]+\.)?(?:dlink|linksys|netgear|asus|tplink|buffalo)\.com$/i, '') // Remove router manufacturer domains
      .replace(/[-_]+/g, ' ') // Replace hyphens/underscores
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      // Original tech prefixes plus new ones
      .replace(
        /^(esp|esp32|esp8266|arduino|raspberry-?pi|rpi|device|node|sensor|localhost|computer|desktop|laptop|server|virtual-?machine|vm|container|pod|docker|macbook|imac|mac-?mini|mac-?pro)/i,
        ''
      ) // Remove tech prefixes
      .replace(
        /^(MSEDGEWIN10|WinDev|Ubuntu|Debian|Fedora|CentOS|RHEL|PopOS|Mint|Arch|Manjaro)/i,
        ''
      ) // Remove OS-specific prefixes
      .replace(/[^\w\s'-]/g, '') // Remove special characters except spaces, hyphens, and apostrophes
      .replace(/^\d+/, '') // Remove leading numbers
      .trim() || 'Device' // Proper case or fallback
  );
}

/**
 * System information interface matching the structure from electron IPC.
 */
export interface SystemInfo {
  /** Machine type from electron IPC (e.g., "laptop", "desktop") */
  machineType: string;
  /** Operating system information from electron IPC */
  operatingSystem: {
    /** Platform identifier (e.g., "darwin", "win32", "linux") */
    platform: string;
    /** OS name (e.g., "macOS", "Windows 10", "Ubuntu") */
    name: string;
    /** Architecture (e.g., "arm64", "x64") */
    arch: string;
    /** OS version (e.g., "15.6") */
    version: string;
    /** OS codename (e.g., "Sequoia") */
    codename?: string;
  };
  /** Computer hostname */
  hostname?: string;
}

/**
 * Converts system information from electron IPC format to iris protobuf enums.
 *
 * This helper method takes the raw system information returned by the electron IPC
 * and converts the `machineType` and `operatingSystem` properties into the appropriate
 * iris protobuf enum values used throughout the application.
 *
 * @param systemInfo - System information from electron IPC
 * @returns Object containing iris-compatible machine type and operating system enums
 *
 * @example
 * ```typescript
 * const systemInfo = {
 *   machineType: "laptop",
 *   operatingSystem: {
 *     platform: "darwin",
 *     name: "macOS",
 *     arch: "arm64",
 *     version: "15.6",
 *     codename: "Sequoia"
 *   }
 * };
 *
 * const { machineType, operatingSystem } = convertSystemInfoToIrisEnums(systemInfo);
 * // machineType: MachineType.MACHINE_TYPE_LAPTOP
 * // operatingSystem: OperatingSystem.OS_MAC
 * ```
 */
export const convertSystemInfoToIrisEnums = (
  systemInfo: SystemInfo
): {
  machineType: MachineType;
  operatingSystem: OperatingSystem;
} => {
  // Convert machine type string to iris enum
  let machineType: MachineType;
  const machineTypeStr = systemInfo?.machineType?.toLowerCase();

  switch (machineTypeStr) {
    case 'laptop':
      machineType = MachineType.MACHINE_TYPE_LAPTOP;
      break;
    case 'desktop':
      machineType = MachineType.MACHINE_TYPE_DESKTOP;
      break;
    default:
      // Default to unknown for unrecognized types
      machineType = MachineType.MACHINE_TYPE_UNKNOWN;
      break;
  }

  // Convert operating system platform to iris enum
  let operatingSystem: OperatingSystem;
  const platform = systemInfo?.operatingSystem?.platform?.toLowerCase();
  const osName = systemInfo?.operatingSystem?.name?.toLowerCase();

  // Primary detection based on platform
  if (platform === 'darwin' || osName?.includes('macos') || osName?.includes('mac os')) {
    operatingSystem = OperatingSystem.OS_MAC;
  } else if (platform === 'win32' || platform === 'windows' || osName?.includes('windows')) {
    operatingSystem = OperatingSystem.OS_WINDOWS;
  } else if (platform === 'linux' || osName?.includes('linux') || osName?.includes('ubuntu')) {
    operatingSystem = OperatingSystem.OS_LINUX;
  } else {
    // Default to unknown for unrecognized platforms
    operatingSystem = OperatingSystem.OS_UNKNOWN;
  }

  return {
    machineType,
    operatingSystem
  };
};
